/*-------------------------------------------------------------------------------*/
/* CAROUSEL WIDGET

This is the master view file for our “carousel” type widgets, which use slider navigation functionality.

Currently this is:
  - Photowall (2 or 3 rows)
  - Mosaic (2 or 3 rows)

In the future we would like to also migrate:
  - Horizontal widget (horizontal.js points to related files)
  - Vertical widget (vertical.js points to related files)
  - Coverflow widget (coverflow.js points to related files)
  - Slideshow widget (slideshow.js points to related files)

It services all device types (desktop/tablet/mobile) and is fully responsive. Any specifically targetted
device functionality should be placed within this file using "options.isMobile" to make the decision. Any
Functionality which will take up a lot of space should be made in a new view file titled carousel_views_mobile.js
which extends these base views below, NOT duplicating its code but inheriting from it.

Everything is powered by Swiper.js, a powerful open source carousel library with an extensive API. The documentation
for this can be found here: https://idangero.us/swiper/api/

Swiper's API has a massive amount of functionality ranging from transition type to accesibility settings, which
makes it an exceptional choice for our use case. It also provides a large variety of events to hook into so that
we can call necessary callbacks where needed. If any functionality is ever missing here, please make a PR to the
open source project, do NOT alter the library locally as it makes upgrading difficult

Any Widget with an AlbumDistill type of "carousel" will use these views. The decision of which "type" of carousel
will be rendered lives in the displayOptions property "carouselOptions.layout". This lets us easily add new subtypes
without having to create new files or altering the albumDistill DB. any subtypes will simply alter Swiper's properties.
*/
/*-------------------------------------------------------------------------------*/
import Swiper from "../../../../vendor/swiper/dist/js/swiper.min.js"
import { decodeUtf8 } from "base/backbone/lib/utilities/helpers";

Pixlee.module('CarouselApp', function(CarouselApp, App) {
    CarouselApp.Layout = App.Views.Layout.extend({
        template: require('./templates/layout.jst.ejs'),
        regions: {
            widgetHeaderRegion: '#widget_header_region',
            interactiveRegion: '#interactive_region',
            carouselWidget: '#carousel_widget'
        },
        behaviors: function() {
            var behaviorHash = {
                CollectionVote: {},
                AppendUploaderTiles: {}
            };

            if (this.options.displayOptions.headerOptions.layout === 'tile') {
                behaviorHash.Upload = {};
            }

            // if there's no upload tiles or if hideIfLessThanIsSet is set, then we need to have the ability to hide empty albums
            var noUploadButton = _.isFalsy(this.options.displayOptions.headerOptions.end_tile_upload_button_background_showHide);
            var setHideIfLessThan = _.isTruthy(App.displayOptions.get('hideIfLessThan')) || _.isTruthy(gon.current_account.extra_fields.hideIfLessThan);
            if (noUploadButton || setHideIfLessThan) {
                behaviorHash.HideEmptyAlbumWidget = {};
            }

            return behaviorHash;
        }
    });
    CarouselApp.Layout.include(['LayoutHelpers', 'AccessibilityHelpers']);

    CarouselApp.SwiperSlide = App.Views.Layout.extend({
        template: require('./templates/_carousel_region_holder.jst.ejs'),
        className: 'swiper-slide',
        modelEvents: {
            'fill:regions': 'onFillRegions'
        },
        ui: {
            'gridHolder': '.grid-holder'
        },
        initialize: function(options) {
            //initialize view instance
            this.layout = options.carouselOptions.layout;
            this.rows = options.carouselOptions.rows;
            this.swiperQueue = options.swiperQueue;
            this.photosCollection = options.photosCollection;
            this.displayOptions = options.displayOptions;
            this.hasEndTile = options.displayOptions.headerOptions.end_tile_upload_button_background_showHide;

            // Calculate total regions and layout class for current random layout
            var targetRegions = this.templateMappings[this.layout].rows[this.rows].regions;
            this.totalRegions = parseInt(_.sample(_.keys(targetRegions)));
            this.layoutClass = _.sample(targetRegions[this.totalRegions].classes);
        },
        onShow: function() {
            this.swiperQueue.push(this.$el[0]);
            // Dynamically create regions for the given amount of rows
            this.initRegions(this.$el.children());
            this.$el.addClass(this.layoutClass);
            // Kick off filling the regions with photo blocks
            this.triggerMethod('fill:regions');
        },
        //Generate individual regions for each photo within the slide depending on row #
        initRegions: function(regions) {
            _.each(regions, function(region, index) {
                this.regionManager.addRegion(region.id, '#' + region.id);
            }.bind(this));
        },
        /* In some cases we remove the template class from a mosaic slide in order to avoid awkward photo positioning;
         * - if there is an uploader tile on the first or last slide
         * - if there aren't enough photos to fill the last slide
         */
        removeTemplateClass: function() {
            this.$el.removeClass(function(i, css) {
                var classes = css.split(' ');
                var remove = [];
                $.each(classes, function(i) {
                    if (this.match(/large_/i)) {
                        remove.push(this);
                    }
                });
                return remove.join(' ');
            });
        },
        //When a new swiper slide is added, we iterate through its regions and inject photo models
        onFillRegions: function() {
            var allRegions = this.regionManager.getRegions();

            // Get all non-rendered photo blocks
            var nonRenderedPhotos = this.photosCollection.reject(function(model) {
                return model.get('isRendered');
            });

            // Get the number of photos in collection, minus uploader tile
            var numPhotos = this.photosCollection.reject(function(model) {
                return model.get('isUploaderTile');
            }).length;

            if (numPhotos <= 0) {
                // passing "false" because there are no photos to render
                this.trigger('slide:rendered', false);
            }

            // Loop through each region and fill them with photo blocks
            _.each(Object.keys(allRegions), function(region, index) {
                var targetRegion = this.regionManager.get(region);
                // if the slide has empty photo regions and no end uploader tile, remove template class to avoid awkward photo positioning
                if (!nonRenderedPhotos.length && _.isFalsy(this.hasEndTile) && _.isFalsy(this.photosCollection.next)) {
                    this.removeTemplateClass();
                }
                // Skip if there are no photo blocks to render OR the target region already has a photo block
                if (!nonRenderedPhotos.length || targetRegion.hasView()) {
                    return true;
                }
                // Render a new photo block in empty region
                var photoModel = nonRenderedPhotos.shift();
                var childView;
                if (photoModel.get('isUploaderTile')) {
                    // remove template class if uploader tile is present so it doesn't render as large photo
                    this.removeTemplateClass();

                    // Add class to slide with uploader tile
                    this.$el.addClass('has_' + photoModel.get('uploaderTileType') + '_uploader_tile');

                    childView = new CarouselApp.CarouselUploaderTile({
                        model: photoModel,
                        displayOptions: this.displayOptions
                    });
                } else {
                    childView = new CarouselApp.PhotoView({
                        model: photoModel,
                        photoQuality: this.displayOptions.photoQuality
                    });
                }

                // indicate small photos in mosaic slide
                if (this.layout === 'mosaic' && index !== 0) {
                    photoModel.set('elementSize', 'small');
                }

                photoModel.set('isRendered', true);
                targetRegion.show(childView);
                //let the parent know that a whole slide was rendered
                if (Object.keys(allRegions).length - 1 === index) {
                    this.trigger('slide:rendered', index + 1);
                }
            }.bind(this));

            // Add a new container of photo blocks if more still need to be rendered
            if (nonRenderedPhotos.length) {
                this.trigger('add:next:container');
            } else {
                this.trigger('append:swiper:queue');
            }
        },
        templateHelpers: function() {
            var totalRegions = this.totalRegions;
            return {
                totalRegions: totalRegions,
            };
        },
        // The template mappings are the possible classes deciding the photo layouts for each chunk of photos
        templateMappings: {
            photowall: {
                rows: {
                    1: {
                        regions: {
                            1: {
                                classes: ['photowall']
                            }
                        }
                    },
                    2: {
                        regions: {
                            2: {
                                classes: ['photowall']
                            }
                        }
                    },
                    3: {
                        regions: {
                            3: {
                                classes: ['photowall']
                            }
                        }
                    }
                }
            },
            mosaic: {
                rows: {
                    2: {
                        regions: {
                            // 3 photos total; 2 small + 1 large
                            3: {
                                classes: [
                                    'large_left',
                                    'large_right'
                                ]
                            },
                        }
                    },
                    3: {
                        regions: {
                            // 6 photos total; 5 small + 1 large
                            6: {
                                classes: [
                                    'large_top_left',
                                    'large_bottom_left',
                                    'large_top_right',
                                    'large_bottom_right'
                                ]
                            },
                        }
                    }
                }
            },
        }
    });

    CarouselApp.CarouselUploaderTile = App.Views.ItemView.extend({
        //Handle the special cases for uploader tile in the first and last slide respectively
        getTemplate: function() {
            if (this.model.get('uploaderTileType') === 'front') {
                return require('../common/templates/v2/_front_header_uploader_tile.jst.ejs');
            }
            return require('../common/templates/v2/_end_uploader_tile.jst.ejs');
        },
        className: 'uploader_tile_wrapper'
    });
    CarouselApp.CarouselUploaderTile.include(['headerHelpers']);

    CarouselApp.PhotoView = App.Views.ItemView.extend({
        getTemplate: function() {
            let isLargeTemplate = _.contains(['xl', 'original'], this.options.photoQuality);
            if (this.layout === 'mosaic' && isLargeTemplate) {
                isLargeTemplate = isLargeTemplate && this.options.model.get('content_type') === 'image';
            }

            if (this.model.get('elementSize') !== 'small' && isLargeTemplate) {
                return require('../common/templates/v2/_xl_photo_item.jst.ejs');
            } else {
                return require('../common/templates/v2/_photo_item.jst.ejs');
            }
        },
        className: 'photo_block',
        tagName: 'div',
        behaviors: {
            Reviews: {},
            Share: {},
            Vote: {},
            Lightbox: {},
            UpdateThumbnailCss: {}
        },
        initialize: function() {
            if (this.model.get('is_starred')) {
                this.$el.addClass('starred');
            }
        },
        templateHelpers: { decodeUtf8 }
    });
    CarouselApp.PhotoView.include(['PhotoViewHelpers', 'ReviewHelpers']);

    CarouselApp.CarouselContainer = App.Views.CompositeView.extend({
        template: require('./templates/_swiper_container.jst.ejs'),
        childViewContainer: '#frame',
        childView: CarouselApp.SwiperSlide,
        className: 'clearfix',
        tagName: 'div',
        ui: {
            frame: '#frame',
            activeSlide: '.swiper-slide-active .img_holder'
        },
        events: {
            'focusin': 'onFocusin'
        },
        collectionEvents: {
            'reset': 'resetSwiper'
        },
        childEvents: {
            'slide:rendered': 'onSlideRendered'
        },
        initialize: function(options) {
            //initialize view instance
            this.photosCollection = options.photosCollection;

            /* We can't assign anything to the this.displayOptions property here
             * because LayoutHelpers is overwriting this.displayOptions.
             * This is mainly an issue when we preview a widget in the DE;
             * it throws an error and can't find the correct displayOptions data.
             * So we assign options.displayOptions as a property that is NOT called this.displayOptions
             */
            this.displayOptionsObject = options.displayOptions;
            this.carouselOptions = options.displayOptions.carouselOptions;

            //initialize values
            this.photosCollection.reviewSelectionOptions = {
                currentModelIndex: 0
            };
            this.swiperQueue = [];
            this.breakpoints = {};
            this.firstLoad = true;
            this.hasNavigated = false;
            this.imagesLoaded = 0;
            this.minNumPaginationDots = 3;
            this.focusedPageIndex = 0;

            /* Max height/width of a photo;
             * must match --widget-photo-size in global_variable_v2.less plus any margin
             * Note that photoSize is also the column width of a photowall slide
             */
            this.photoSize = 260;
            this.mosaicSlideWidth = this.photoSize * 3;

            /* minimum number of photo columns to display
             * must match @columns value in stylesheets/carousel/_variables.less
             */
            this.minColumns = 3.5;
        },
        childViewOptions: function() {
            return {
                photosCollection: this.photosCollection,
                swiperQueue: this.swiperQueue,
                carouselOptions: this.displayOptionsObject.carouselOptions,
                displayOptions: this.displayOptionsObject
            };
        },
        onDestroy: function() {
            this.photosCollection.off('reset');
        },
        onShow: function() {
            $('#widget').attr('alt', App.altText);
            this.photosCollection.fetch();
            this.getBreakpoints();
            this.initSwiper();
        },
        // handle focus after using arrows to navigate
        onFocusin: function(e) {
            // if on a page that does not have the currently focused element
            if (this.focusedPageIndex !== this.swiper.activeIndex) {
                e.preventDefault();
                var activePhoto = this.ui.frame
                    .find(this.ui.activeSlide.selector)
                    .get(0);
                activePhoto.focus();
                this.focusedPageIndex = this.swiper.activeIndex;
            }
        },
        //Clear all swiper properties whenever the marionette collection resets (generally for interactive widget)
        resetSwiper: function() {
            if (this.swiper) {
                this.swiper.activeIndex = 0;
                this.swiper.removeAllSlides();
                this.swiperQueue = [];
            }
        },
        //This function is fired every time a new Swiper slide finishes rendering. Helful for appropriate callbacks.
        onSlideRendered: function(childView, numPhotos) {

            // if there are no images to load, apply a class that can be used to adjust container height
            if (_.isFalsy(numPhotos)) {
                this.ui.frame.addClass('no_images_loaded');
                return;
            }

            //if the photos per slide (numPhotos) does not evenly divide into the collection length, there will be a remainder
            //which does not render until a load more event occurs (we never render "half" slides unless it's the last one)
            //here we account for that by checking if the "unrendered" photos is less than the number that render each slide
            var stillToRender = this.photosCollection.countBy('isRendered').undefined;
            var remainder = stillToRender < numPhotos ? stillToRender : 0;

            //The initial number of photos loaded is the smaller of photos available and page size
            var photosInInitialLoad = Math.min(this.photosCollection.perPage, this.photosCollection.length);
            this.imagesLoaded += numPhotos;

            //This is the point where we're "done" rendering Swiper after initial page load
            //we can use this as a "done rendering" pseudo event
            if (_.isTruthy(this.firstLoad) && (this.imagesLoaded + remainder) >= photosInInitialLoad) {
                this.firstLoad = false;

                //send to the back of the queue because the final redraw is inconsistent
                setTimeout(function() {
                    this.initialWidgetLoadComplete();
                    this.swiper.activeIndex = 0;
                }.bind(this), 0);
            }
        },
        //Switches between "smooth" momentum style swiping and "snapping" swiping when switched in Design editor
        //Code that triggers this lives in design_editor_collection_helpers.js
        updateSwipingMode: function(smoothSwiping) {
            this.swiper.params.freeMode = smoothSwiping;
            this.swiper.params.freeModeMomentum = smoothSwiping;
            this.swiper.params.preventInteractionOnTransition = !smoothSwiping;
        },
        //Initialize the Swiper instance with all of its properties. This only happens once
        initSwiper: function(regionWidth) {
            var isMobileDE = _.isTruthy(gon.previewMode && gon.isMobile);
            var smoothSwiping = this.carouselOptions.smoothSwiping;

            this.swiper = new Swiper('#frame', {
                setWrapperSize: true,
                slidesPerView: 'auto',
                simulateTouch: isMobileDE,
                speed: 600,
                breakPointsInverse: false,
                centerInsufficientSlides: true,
                keyboard: true,
                a11y: true,
                freeMode: smoothSwiping,
                freeModeMomentum: smoothSwiping,
                preventInteractionOnTransition: !smoothSwiping,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                pagination: {
                    el: '.swiper-pagination',
                    dynamicBullets: true,
                    dynamicMainBullets: 3,
                    renderBullet: this.renderPaginationBullets,
                },
                on: {
                    progress: this.onHandleLoadMore.bind(this),
                    slideNextTransitionStart: this.onNavRight.bind(this),
                    paginationRender: this.onPaginationRender,
                    paginationUpdate: this.centerPaginationIndicator
                },
                breakpoints: this.breakpoints
            });
        },
        //Collection of breakpoints determine how many slides to swipe through
        getBreakpoints: function() {
            if (this.carouselOptions.layout === 'mosaic') {
                this.breakpoints = this.generateBreakpoints(this.mosaicSlideWidth);
            } else {
                this.breakpoints = this.generateBreakpoints(this.photoSize);
            }
        },
        //calculate the breakpoints for swiper's slides based on the viewport size. this makes sliding responsive
        //and prevents too many slides from being swiped through at smaller screen sizes
        generateBreakpoints: function(slideWidth) {
            var breakpoints = {};
            var minViewportWidth = this.photoSize * this.minColumns;
            var maxViewportWidth = this.photoSize * 15; // about 3000px

            for (var i = minViewportWidth; i < maxViewportWidth; i = i + 100) {
                var breakPoint = {};
                var denominator = this.carouselOptions.layout === 'photowall' ? slideWidth * 2 : slideWidth;
                breakPoint[i] = {
                    slidesPerGroup: Math.floor(i / denominator)
                };
                _.extend(breakpoints, breakPoint);
            }
            return breakpoints;
        },
        //If there's too many slides in view, we want to have the pagination indicator be the middle not the first
        //This wil prevent the "active bullet" from always looking like it's the 3rd one as the bulets move
        centerPaginationIndicator: function() {
            var isNotFirstTwoSlides = this.snapIndex > 2;
            var isNotLastTwoSlides = this.snapIndex < this.pagination.bullets.length - 1;
            var isViewportLarge = this.slidesPerViewDynamic() > 2;

            //For these cases, we want the active bullet to be 1 further ahead than the default so it
            //stays as the middle of the 3 "main" bullets
            if (isViewportLarge && isNotFirstTwoSlides && isNotLastTwoSlides) {
                var activeClass = 'swiper-pagination-bullet-active';
                this.pagination.bullets.removeClass(activeClass);
                this.pagination.bullets.eq(this.snapIndex + 1).addClass(activeClass);
            }
        },
        //Helper to prevent single page widgets from having navigation bullets (hide 1 bullet)
        onPaginationRender: function() {
            if (this.pagination.bullets.length === 1) {
                this.pagination.bullets.addClass('inactive');
            }
        },
        //Helper to customize how pagination bullets render each time
        renderPaginationBullets: function(index, className) {
            //make sure there's no scaling animation on render, but bring them back later. this is a default swiper class we want to override
            setTimeout(function() {
                this.pagination.bullets.removeClass('pagination-bullet-loading');
            }.bind(this), 100);

            return '<span class="pagination-bullet-loading ' + className + '"></span>';
        },
        //As progress is made in the widget, we decide when we need to load more photos. This is done dynamically,
        //As the gallery gets bigger, we need to get "further" to make more network calls.
        onHandleLoadMore: function(progress) {
            if (!this.photosCollection.next || this.isLoading) {
                return;
            }
            var fillViewPort = this.swiper && this.swiper.isEnd;
            var numPaginationDots = this.swiper.pagination.bullets.length;

            //percentage of gallery which leaves 5 dots remaining
            //NOTE: this will forcibly load more pages on initial load if the default perPage is too small
            //this is usually the case with 3 row photowall, so important to keep in mind
            var progressThreshold = 1 - ((1 / numPaginationDots) * this.minNumPaginationDots);
            var loadNextPage = progress >= progressThreshold;

            if (fillViewPort || loadNextPage) {
                this.isLoading = true;
                this.photosCollection.nextPage({
                    success: this.loadMoreSuccess.bind(this)
                });
            }
        },
        //Call back wehn network call to load more succeeds
        loadMoreSuccess: function() {
            this.isLoading = false;
            if (_.isTruthy(this.hasNavigated)) {
                App.commands.execute('load:more', this.photosCollection.pluck('album_photo_id'));
            }
        },
        //Once the swiper slides have been created and filled, push them into the carousel
        onChildAppendSwiperQueue: function() {
            this.swiper.appendSlide(this.swiperQueue);
        },
        //Dynamically calculate which photos are actually in the viewport at a given moment in time and return their indices
        getPhotosInViewIndex: function() {
            var slides = this.swiper.slidesPerViewDynamic();
            var photosPerSlide = this.carouselOptions.rows;

            //photowall slides are vertical columns of photos, so slides x rows gives us photos in view
            //mosaic slides have a fixed number of photos N depending on row amount, so slides x N gives us photos in view
            if (this.carouselOptions.layout === 'mosaic') {
                photosPerSlide = this.carouselOptions.rows === 3 ? 6 : 3;
            }

            var itemIndex = {
                firstItem: this.swiper.realIndex,
                lastItem: (slides * photosPerSlide) - 1
            };

            return itemIndex;
        },
        //Analytics event for Widget Loaded
        initialWidgetLoadComplete: function() {
            var itemIndex = this.getPhotosInViewIndex();
            App.commands.execute('opened:carousel', itemIndex, this.photosCollection, this.carouselOptions);
            this.firstLoad = false;
        },
        //Analytics event for Interacted (Nav right)
        onNavRight: function() {
            var itemIndex = this.getPhotosInViewIndex();
            App.commands.execute('nav:right', itemIndex, this.photosCollection);

            //After first navigation, we want to load photos more aggresively
            this.hasNavigated = true;
            this.minNumPaginationDots = 5;
        },
        templateHelpers: function() {
            var carouselOptions = this.options.displayOptions.carouselOptions;
            return {
                getLayoutClass: function() {
                    return carouselOptions.layout + '-' + carouselOptions.rows;
                }
            };
        }
    });
    CarouselApp.CarouselContainer.include(['tabindexHelpers', 'designEditorCollectionHelpers', 'socialShareHelpers']);

    CarouselApp.HeaderView = App.Views.ItemView.extend({
        template: require('../common/templates/v2/_header.jst.ejs'),
        id: 'widget_header',
        behaviors: {
            Upload: {},
            ShowPdpCaption: {}
        }
    });
    CarouselApp.HeaderView.include(['StandaloneHeaderHelpers']);

});
